/** lag-slideout
 * 
 * PROPERTIES/ATTRIBUTES
 *      opened: Boolean // Allows you to know the state of the slideout, dont use to set the state, use open() and close() methods instead.
 *      persistent: Boolean // Doesn't allow clicking on backdrop to close the slideout.
 *      allowEsc: Boolean // Allow the 'esc' key to close the slideout.
 *
 * EVENTS
 *      'opened' // State has changed to opened (UI transition starts)
 *      'closed' // State has changed to closed (UI transition starts)
 *      'transitioned' // UI transition finishes (after state change)
 *      'content-created' // One-time event emitted (only once) when slideout stamps any slotted <template> elemenst that have the slideout-autostamp attribute.
 *
 * EXAMPLE USAGE:
 *      <lag-slideout persistent>
 *          <template slideout-autostamp>Some content...</template>
 *          <template slot="footer" slideout-autostamp>
 *              <button slideout-close>Close</button><!-- Elements with slideout-close attribute which produce 'tap' events will close the slideout automatically. -->
 *          </template>
 *      </lag-slideout>
 */

import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import { afterNextRender } from '@polymer/polymer/lib/utils/render-status';

import '@polymer/app-layout/app-drawer/app-drawer.js';

import { Localizer } from '../extendibles/Localizer.js';
import { defineElement } from '../../utility/defineElement.js';
import * as tools from '../../utility/tools.js';

import { template } from './lag-slideout.html.js';

defineElement(class extends Localizer(PolymerElement) {

    static get is() { return 'lag-slideout'; }
    static get template() { return template; }

    static get properties() {
        return {
            opened: {
                type: Boolean,
                value: false,
                readOnly: true,
                reflectToAttribute: true
            },
            persistent: {
                type: Boolean,
                value: false,
                reflectToAttribute: true
            },
            allowEsc: {
                type: Boolean,
                value: false
            }
        };
    }

    // ========================================================================

    constructor() {
        super();
        this._skipEvent = false;
    }
    connectedCallback() {
        super.connectedCallback();

        this._initAutoCloseSlideoutEventHandler();
    }
    ready() {
        super.ready();

        this.$.drawer.addEventListener('opened-changed', ev => {
            const opened = ev.detail.value;
            this._setOpened(opened); // Ensure 'opened' prop is synced (read-only prop).

            if (!this._skipEvent) {
                if (opened)
                    this.dispatchEvent(new Event('opened'));
                else
                    this.dispatchEvent(new Event('closed'));
            }
        });

        this.$.drawer.addEventListener('app-drawer-transitioned', () => {
            if (!this._skipEvent) {
                this.dispatchEvent(new Event('transitioned'));
            }

            this._skipEvent = false;
        });

        this.toggleAttribute('inner-slideout', tools.ele.path(this).includes('lag-slideout')); // For styling.
    }

    // ========================================================================

    /**
     * Arguments will be used when 'slideout-autostamp' template elements are initialized (on first 'open' call).
     * If no 'slideout-autostamp' templates exist then this will not be used.
     * The 'runAfterInit' parameter must be a function, it will be called after initialization. See the 'callWithSlotted' method for more info.
     * The 'dataForTemplates' is optional and can either be an object with data or a callback that takes a template as an argument
     * and returns an object to be used when instantiating (stamping) that template.
     * @param {Function} runAfterInit Optional, ({ content }) => { doSomeInitStuffWith(content.$.something); }
     * @param {object|Function} dataForTemplates Optional, { data } OR (templateElementBeforeStamp) => { return { data }; }
     */
    onInit(runAfterInit, dataForTemplates) {
        this._runAfterInit = runAfterInit;
        this._dataForTemplates = dataForTemplates;
    }

    /**
     * Open the slideout. If already open the code WILL STILL RUN, this allows for re-initialization/etc.
     * If 'skipEvent' is true then no events will be fired for this invokation. Normally DO NOT specify this parameter.
     * If 'runBeforeOpen' is provided then it will be called before opening, and if it returns false then the open will be aborted.
     * @param {object|boolean|Function} options { skipEvent:false, runBeforeOpen:null }
     * Alternatively can specify a boolean and will be used as skipEvent, or a Function and will be used as runBeforeOpen.
     * See the 'callWithSlotted' method documentation for more info on the callback functionality.
     */
    open(options) {
        options = {
            skipEvent: (typeof options === 'boolean') ? options : !!(options && options.skipEvent),
            runBeforeOpen: (typeof options === 'function') ? options : (options && options.runBeforeOpen) || null
        };

        return this.callWithSlotted(options.runBeforeOpen).then(result => {
            if (result === false) // If explicit false value then we abort the open operation.
                return;

            // Open slideout...

            this.$.drawer._boundEscKeydownHandler = this._escKeyHandler; // Hack to prevent ESC key from closing drawer.

            this._skipEvent = !!options.skipEvent;
            this.$.scroller.scrollTop = 0;

            this.$.drawer.hidden = false;
            afterNextRender(this.$.drawer, _ => this.$.drawer.open()); // Wait after render so [hidden] is updated, otherwise drawer slideout animation would happen before element is actually visible.
        });
    }
    /**
     * Close the slideout (if not already closed). If already closed then nothing will happen.
     * If 'skipEvent' is true then no events will be fired for this invokation. Normally DO NOT specify this parameter.
     * If 'runBeforeClose' is provided then it will be called before closing, and if it returns false then the close will be aborted.
     * @param {object|boolean|Function} options Optional: { skipEvent:boolean, runBeforeClose:Function }
     * Alternatively can specify a boolean and will be used as skipEvent, or a Function and will be used as runBeforeClose.
     * See the 'callWithSlotted' method documentation for more info on the callback functionality.
     */
    close(options) {
        if (!this.opened)
            return;

        options = {
            skipEvent: (typeof options === 'boolean') ? options : !!(options && options.skipEvent),
            runBeforeClose: (typeof options === 'function') ? options : (options && options.runBeforeClose) || null
        };

        return this.callWithSlotted(options.runBeforeClose).then(result => {
            if (result === false) // If explicit false value then we abort the close operation.
                return;

            // Close slideout...

            this._skipEvent = !!options.skipEvent;

            this.$.drawer.close();
            this.$.drawer.hidden = true;
        });
    }
    /**
     * Toggle the slideout.
     * If 'skipEvent' is true then no events will be fired for this invokation. Normally DO NOT specify this parameter.
     * @param {object} options Optional: { skipEvent:boolean }.
     * Alternatively can specify a boolean and will be used as skipEvent.
     */
    toggle(options) {
        options = {
            skipEvent: (typeof options === 'boolean') ? options : !!(options && options.skipEvent)
        };

        this._skipEvent = options.skipEvent;
        this.$.drawer.toggle();
    }

    /**
     * Run the provided callback, it will be passed an argument containing slotted template fragment(s).
     * The argument will be an object in the form { content, footer }. Only <template> elements with the
     * 'slideout-autostamp' attribute will be instantiated (on first 'open' call) and provided in this argument.
     * If there is only a single autostamp template per slot then the property will be a handle directly to the
     * document-fragment, otherwise it will be a collection of document-fragments accessible via properties
     * named the same as the source template ID attributes (if one was specified).
     * @param {Function} callback ({ content, footer }) => doSomethingWith(content, footer)
     * @returns {Promise} A promise that resolves to the return value of the callback.
     */
    callWithSlotted(callback) {
        // One-time init of slotted templates (if already init'd then this call will do nothing).
        // Always return Promise that resolves after initialization (and any init callback specified via onInit).
        return this._initSlots().then(_ => {
            return (typeof callback === 'function')
                ? callback(this._stampedTemplateFrags || {})
                : undefined;
        });
    }
    /**
     * Checks if the slideout is open, returns a boolean indicating true:open or false:closed.
     * @param {Function} callback An optional callback that will be called ONLY if the slideout is already OPEN.
     * See the 'callWithSlotted' method documentation for more info on the callback functionality.
     * @param {boolean} returnCallbackResultPromise Default:false, if false then the call will return true (if slideout is open),
     * if parameter is true then the call will return a promise wrapping the callback's return value.
     * If the slideout is NOT open then false is returned either way.
     * @returns {boolean} true/false indicating if the slideout is open, this is the same value as the 'opened' property.
     */
    isOpen(callback, returnCallbackResultPromise) {
        if (!this.opened)
            return false; // Is NOT open

        const prom = this.callWithSlotted(callback);
        if (returnCallbackResultPromise)
            return prom; // Returning promise with callback result rather than opened boolean.
        else
            return true; // Is open
    }

    // ========================================================================
    // private Helpers

    _escKeyHandler(ev) {
        if (this.allowEsc) {
            this.$.drawer._escKeydownHandler(ev);
        }
    }

    _initAutoCloseSlideoutEventHandler() {
        this.addEventListener('tap', ev => {
            if (!ev || !ev.target || ev.target.nodeType !== 1)
                return;

            if (!ev.target.hasAttribute('slideout-close'))
                return;


            this.close();
        });
    }

    // ========================================================================
    // private Helpers for stamping slotted templates

    // One-time initialization of slotted <template> elements (if any),
    // and then call the onInit callback (if provided).
    _initSlots() {
        if (this._slotsInitialized)
            return Promise.resolve();

        this._slotsInitialized = true;

        const contentTemplates = this._getSlottedAutoStampTemplates(this.$.contentSlot);
        const footerTemplates = this._getSlottedAutoStampTemplates(this.$.footerSlot);

        this._stampedTemplateFrags = {
            // Note: dataForTemplates is optionally set in onInit call.
            content: this._stampTemplates(contentTemplates, this._dataForTemplates),
            footer: this._stampTemplates(footerTemplates, this._dataForTemplates)
        };

        this.dispatchEvent(new Event('content-created'));

        return this.callWithSlotted(this._runAfterInit);
    }

    _getSlottedAutoStampTemplates(slot) {
        const filterAutoStampTemplateElements = (ele) =>
            (ele.tagName === 'TEMPLATE') && ele.hasAttribute('slideout-autostamp');

        return slot.assignedElements().filter(filterAutoStampTemplateElements);
    }

    _stampTemplates(templates, initData) {
        if (!templates || !templates.length) return;

        // If a single template WITHOUT an ID then we return the instantiated fragment itself.
        // Else if either multiple templates or the single template has an ID then return an object with
        // the instantiated fragments accessible via properties matching the template IDs.

        const fragsCollection = {};

        for (const t of templates) {
            const frag = tools.template.stampAndReplace(t, this._normalizeInitDataForTemplates(initData, t));

            // Only single template so return it's instantiation directly rather than a collection of them.
            if (templates.length === 1) {
                return frag;
            }

            // If the template has an ID then it will be available via the collection,
            // otherwise it wont be availble.. too bad, add an ID if you want to have access to it.
            if (t.id) {
                fragsCollection[t.id] = frag;
            }
        }

        return fragsCollection;
    }

    _normalizeInitDataForTemplates(initData, template) {
        // If initData is a function then we call it,
        // using it as a callback to get actual initData based on the template.
        if (typeof initData === 'function') {
            initData = initData(template);
        }
        // Otherwise we just use the initData object/data itself.

        // Always add localization property.
        return Object.assign({ localize: this.localize }, initData);
    }

});